// The interaction timeline: every email, call, text, meeting and note as a reading pane
// (list of conversations + the selected one), with search and filters. Emails in a thread
// form one conversation.

import DOMPurify from 'dompurify';
import { useMutation } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Mail, MessageSquare, Paperclip, Phone, StickyNote, Users } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { OUTCOME_LABEL, type Outcome } from '../lib/activity';
import { ActivityActions } from './ActivityActions';
import { dateTime } from '../lib/format';
import { desktopLink, getOutlookOpen } from '../lib/outlookOpen';
import { supabase, type Tables } from '../lib/supabase';
import { cx, Empty, Input, Pill, Select } from './ui';

export type TimelineItem = Pick<
  Tables<'interactions'>,
  'id' | 'interaction_type' | 'direction' | 'occurred_at' | 'subject' | 'body_html' | 'body_text' | 'snippet' | 'duration_seconds' | 'source' | 'source_link' | 'thread_id' | 'contact_id'
> &
  Partial<Pick<Tables<'interactions'>, 'participants' | 'has_attachments' | 'body_storage' | 'mail_account_id' | 'external_id' | 'outcome' | 'reminder_id'>> & { contactName?: string };

export const ICON = { email: Mail, call: Phone, text: MessageSquare, meeting: Users, note: StickyNote } as const;
const TYPES = ['email', 'call', 'text', 'meeting', 'note'] as const;

type Person = { name: string | null; email: string; role: 'from' | 'to' | 'cc' };

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, FORBID_TAGS: ['style'] });
}

/** "Dr. Patel → Brett, Ken Parker" for synced emails. */
export function People({ participants }: { participants: unknown }) {
  const ps = Array.isArray(participants) ? (participants as Person[]) : [];
  if (!ps.length) return null;
  const label = (p: Person) => <span title={p.email}>{p.name || p.email}</span>;
  const from = ps.find((p) => p.role === 'from');
  const to = ps.filter((p) => p.role !== 'from');
  return (
    <div className="mt-0.5 truncate text-xs text-slate-500">
      {from && label(from)}
      {to.length > 0 && (
        <>
          {' → '}
          {to.slice(0, 4).map((p, i) => (
            <span key={p.email + p.role}>
              {i > 0 && ', '}
              {label(p)}
            </span>
          ))}
          {to.length > 4 && ` +${to.length - 4}`}
        </>
      )}
    </div>
  );
}

export function Body({ item }: { item: TimelineItem }) {
  const [open, setOpen] = useState(false);
  // Text is stored; the formatted version is fetched from Outlook on demand (Brett chose text-only, 2026-10-22).
  const fetched = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('mail-message', { body: { interaction_id: item.id } });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const msg = ctx ? ((await ctx.json().catch(() => null)) as { error?: string } | null)?.error : null;
        throw new Error(msg ?? error.message);
      }
      return data as { html: string; from: 'stored' | 'outlook'; ms?: number };
    },
  });
  const storedHtml = useMemo(() => (item.body_html ? sanitize(item.body_html) : null), [item.body_html]);
  const fetchedHtml = useMemo(() => (fetched.data?.html ? sanitize(fetched.data.html) : null), [fetched.data]);
  const html = storedHtml ?? fetchedHtml;
  const text = item.body_text ?? item.snippet;
  const synced = !!item.mail_account_id;
  if (!html && !text && !synced) return null;
  const long = (text?.length ?? 0) > 400 || (html?.length ?? 0) > 800;
  return (
    <div className="mt-1.5">
      <div className={cx(!open && long && 'max-h-28 overflow-hidden [mask-image:linear-gradient(to_bottom,black_60%,transparent)]')}>
        {html ? <div className="email-body" dangerouslySetInnerHTML={{ __html: html }} /> : <p className="whitespace-pre-wrap text-sm text-slate-700">{text}</p>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {long && (
          <button className="font-medium text-indigo-600 hover:underline" onClick={() => setOpen((o) => !o)}>
            {open ? 'Show less' : 'Show more'}
          </button>
        )}
        {synced && !storedHtml && !fetchedHtml && (
          <button
            className="font-medium text-indigo-600 hover:underline disabled:text-slate-400"
            disabled={fetched.isPending}
            onClick={() => fetched.mutate(undefined, { onSuccess: () => setOpen(true) })}
          >
            {fetched.isPending ? 'Loading formatted…' : 'Show formatted'}
          </button>
        )}
        {fetched.error && <span className="text-rose-600">{(fetched.error as Error).message}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reading pane (Brett picked it in the Timeline lab, 2026-10-16): conversations on the
// left, the selected one on the right. Stacks on a phone.
// ---------------------------------------------------------------------------

export interface Conversation {
  key: string;
  kind: string;
  items: TimelineItem[]; // newest first
  latest: TimelineItem;
  subject: string;
}

/** Emails grouped by thread; everything else stands alone. Newest conversation first. */
export function conversations(items: TimelineItem[]): Conversation[] {
  const map = new Map<string, TimelineItem[]>();
  for (const i of items) {
    const key = i.interaction_type === 'email' && i.thread_id ? `t:${i.thread_id}` : `i:${i.id}`;
    map.set(key, [...(map.get(key) ?? []), i]);
  }
  return [...map.entries()]
    .map(([key, list]) => {
      const sorted = [...list].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
      const latest = sorted[0]!;
      const subject = (sorted.find((x) => x.subject)?.subject ?? '').replace(/^((re|fw|fwd):\s*)+/i, '') || (latest.interaction_type === 'email' ? '(no subject)' : (latest.body_text ?? latest.snippet ?? '').replace(/\s+/g, ' ').slice(0, 120) || cap(latest.interaction_type));
      return { key, kind: latest.interaction_type, items: sorted, latest, subject };
    })
    .sort((a, b) => b.latest.occurred_at.localeCompare(a.latest.occurred_at));
}

/** Every word must appear somewhere in the subject, text or people. */
export function matches(i: TimelineItem, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [i.subject, i.body_text, i.snippet, i.contactName, JSON.stringify(i.participants ?? '')].join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function Dir({ item }: { item: TimelineItem }) {
  if (item.direction === 'in') return <ArrowDownLeft className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-label="received" />;
  if (item.direction === 'out') return <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="sent" />;
  return <span className="w-3.5 shrink-0" />;
}

function shortDate(ts: string): string {
  const d = new Date(ts);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: '2-digit' }) });
}

function fromName(i: TimelineItem): string {
  if (i.interaction_type !== 'email')
    return cap(i.interaction_type) + (i.outcome ? ` · ${OUTCOME_LABEL[i.outcome as Outcome] ?? i.outcome}` : '') + (i.duration_seconds ? ` · ${Math.round(i.duration_seconds / 60)} min` : '');
  const from = (Array.isArray(i.participants) ? (i.participants as Person[]) : []).find((p) => p.role === 'from');
  return i.direction === 'out' ? 'You' : from?.name || from?.email || i.contactName || 'Them';
}

function Row({ c, selected, onClick, showContact }: { c: Conversation; selected: boolean; onClick: () => void; showContact?: boolean }) {
  const Icon = ICON[c.kind as keyof typeof ICON] ?? StickyNote;
  const sender = fromName(c.latest);
  const who = showContact ? [...new Set(c.items.map((i) => i.contactName).filter((n) => n && n !== sender))].join(', ') : '';
  return (
    <button onClick={onClick} className={cx('block w-full px-3 py-2 text-left text-sm hover:bg-slate-50', selected && 'bg-indigo-50/70')}>
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <Dir item={c.latest} />
        <span className="min-w-0 flex-1 truncate text-slate-600">
          {sender}
          {who && <span className="text-indigo-600"> · {who}</span>}
        </span>
        {c.items.some((i) => i.has_attachments) && <Paperclip className="h-3 w-3 shrink-0 text-slate-400" aria-label="has attachments" />}
        <span className="shrink-0 text-xs text-slate-500">{shortDate(c.latest.occurred_at)}</span>
      </div>
      <div className="truncate pl-[3.25rem] font-medium text-slate-900">
        {c.subject}
        {c.items.length > 1 && <span className="ml-1 text-xs font-normal text-slate-500">({c.items.length})</span>}
      </div>
    </button>
  );
}

/** Opens the email in Outlook on the web, or in classic desktop Outlook if this device is set up for it (Settings). */
function OpenInOutlook({ item }: { item: TimelineItem }) {
  if (!item.source_link) return null;
  const link = 'text-indigo-600 hover:underline';
  const web = (
    <a className={link} href={item.source_link} target="_blank" rel="noreferrer">
      {getOutlookOpen() === 'desktop' ? 'web' : 'Open in Outlook'}
    </a>
  );
  if (getOutlookOpen() === 'web' || !item.external_id?.startsWith('<')) return <div className="mt-1 text-xs">{web}</div>;
  return (
    <div className="mt-1 flex items-center gap-x-2 text-xs">
      <a className={cx('font-medium', link)} href={desktopLink(item.external_id)} title="Opens in classic Outlook on this computer">
        Open in Outlook
      </a>
      <span className="text-slate-400">·</span>
      {web}
    </div>
  );
}

/** The selected conversation: earlier messages as one-liners (click to open), newest open. */
function Thread({ c, showContact }: { c: Conversation; showContact?: boolean }) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set([c.latest.id]));
  const toggle = (id: string) => setOpenIds((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set([...s, id])));
  return (
    <div className="space-y-1">
      {[...c.items].reverse().map((m) =>
        openIds.has(m.id) ? (
          <div key={m.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <Dir item={m} />
              <span className="font-medium text-slate-700">{fromName(m)}</span>
              {showContact && m.contactName && m.contactName !== fromName(m) && (
                <Link className="text-indigo-600 hover:underline" to={`/contacts/${m.contact_id}`}>
                  {m.contactName}
                </Link>
              )}
              {m.has_attachments && <Paperclip className="h-3 w-3" aria-label="has attachments — open in Outlook to see them" />}
              <span className="ml-auto">{dateTime(m.occurred_at)}</span>
              <ActivityActions item={m} />
              {c.items.length > 1 && (
                <button className="text-indigo-600 hover:underline" onClick={() => toggle(m.id)}>
                  collapse
                </button>
              )}
            </div>
            <People participants={m.participants} />
            <Body item={m} />
            <OpenInOutlook item={m} />
          </div>
        ) : (
          <button key={m.id} onClick={() => toggle(m.id)} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-slate-600 hover:bg-slate-50">
            <Dir item={m} />
            <span className="w-24 shrink-0 truncate">{fromName(m)}</span>
            <span className="min-w-0 flex-1 truncate text-slate-500">{(m.snippet ?? m.body_text ?? '').replace(/\s+/g, ' ')}</span>
            <span className="shrink-0">{shortDate(m.occurred_at)}</span>
          </button>
        ),
      )}
    </div>
  );
}

/** `showContact` (company and deal pages): show whose activity it is, and filter by contact. */
export function Timeline({ items, showContact }: { items: TimelineItem[]; showContact?: boolean }) {
  const [q, setQ] = useState('');
  const [type, setType] = useState<string | null>(null);
  const [contact, setContact] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const pane = useRef<HTMLDivElement>(null);

  const people = useMemo(() => {
    const m = new Map<string, { name: string; n: number }>();
    for (const i of items) if (i.contact_id) m.set(i.contact_id, { name: i.contactName ?? 'Unknown', n: (m.get(i.contact_id)?.n ?? 0) + 1 });
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n);
  }, [items]);
  const byContact = contact ? items.filter((i) => i.contact_id === contact) : items;
  const convs = useMemo(() => conversations(byContact.filter((i) => (!type || i.interaction_type === type) && matches(i, q))), [byContact, q, type]);
  const selected = convs.find((c) => c.key === sel) ?? convs[0] ?? null;

  if (!items.length) return <Empty>No activity yet.</Empty>;

  function pick(key: string) {
    setSel(key);
    // On a phone the pane sits below the list: bring it into view.
    if (window.matchMedia('(max-width: 1023px)').matches) requestAnimationFrame(() => pane.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search emails, calls, notes…" className="sm:!w-64" type="search" />
        {showContact && people.length > 1 && (
          <Select value={contact} onChange={(e) => setContact(e.target.value)} className="sm:!w-56" aria-label="Filter by contact">
            <option value="">All contacts ({people.length})</option>
            {people.map(([id, p]) => (
              <option key={id} value={id}>
                {p.name} ({p.n})
              </option>
            ))}
          </Select>
        )}
        <button onClick={() => setType(null)}>
          <Pill color={type === null ? 'indigo' : 'gray'}>All {byContact.length}</Pill>
        </button>
        {TYPES.map((t) => {
          const n = byContact.filter((i) => i.interaction_type === t).length;
          return n ? (
            <button key={t} onClick={() => setType(type === t ? null : t)}>
              <Pill color={type === t ? 'indigo' : 'gray'}>
                <span className="capitalize">{t}s</span>&nbsp;{n}
              </Pill>
            </button>
          ) : null;
        })}
      </div>
      <div className="grid gap-3 lg:grid-cols-5">
        <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 lg:col-span-2 lg:max-h-[32rem]">
          {convs.map((c) => (
            <li key={c.key}>
              <Row c={c} selected={selected?.key === c.key} onClick={() => pick(c.key)} showContact={showContact} />
            </li>
          ))}
          {convs.length === 0 && <Empty>No matches.</Empty>}
        </ul>
        <div ref={pane} className="scroll-mt-16 overflow-y-auto rounded-md border border-slate-200 p-3 lg:col-span-3 lg:max-h-[32rem]">
          {selected ? (
            <>
              <div className="mb-2 text-sm font-semibold text-slate-900">{selected.subject}</div>
              <Thread key={selected.key} c={selected} showContact={showContact} />
            </>
          ) : (
            <Empty>Nothing selected.</Empty>
          )}
        </div>
      </div>
    </div>
  );
}
